import { Injectable } from '@angular/core';

export type GoogleOsintSearchType = 'username' | 'email' | 'name' | 'phone';

export interface GoogleOsintQueryVariant {
  id: string;
  label: string;
  query: string;
}

@Injectable({
  providedIn: 'root',
})
export class GoogleOsintQueryBuilder {
  build(
    type: GoogleOsintSearchType | string,
    value: string | null | undefined,
  ): string[] {
    return this.buildVariants(type, value).map((variant) => variant.query);
  }

  buildVariants(
    type: GoogleOsintSearchType | string,
    value: string | null | undefined,
  ): GoogleOsintQueryVariant[] {
    switch (type) {
      case 'username':
        return this.buildUsernameQueries(value);
      case 'email':
        return this.buildEmailQueries(value);
      case 'name':
        return this.buildNameQueries(value);
      case 'phone':
        return this.buildPhoneQueries(value);
      default:
        return [];
    }
  }

  private buildUsernameQueries(
    username: string | null | undefined,
  ): GoogleOsintQueryVariant[] {
    const value = this.sanitize(username);
    if (!value) {
      return [];
    }

    const formats = this.buildAllUsernameFormats(value);
    if (!formats.length) {
      return [];
    }

    return this.uniqueVariants([
      {
        id: 'all-formats',
        label: 'All formats',
        query: this.buildQuotedOrQuery(formats),
      },
    ]);
  }

  private buildEmailQueries(
    email: string | null | undefined,
  ): GoogleOsintQueryVariant[] {
    const value = this.sanitize(email).toLowerCase();
    if (!value) {
      return [];
    }

    // Google matches emails only when they are unquoted. Quoted exact-match
    // and `"email" | email` both return zero hits.
    return this.uniqueVariants([
      {
        id: 'exact',
        label: 'Exact',
        query: value,
      },
    ]);
  }

  private buildNameQueries(
    name: string | null | undefined,
  ): GoogleOsintQueryVariant[] {
    const value = this.sanitize(name);
    if (!value) {
      return [];
    }

    const formats = this.buildAllNameFormats(value);
    if (!formats.length) {
      return [];
    }

    return this.uniqueVariants([
      {
        id: 'all-formats',
        label: 'All formats',
        query: this.buildQuotedOrQuery(formats),
      },
    ]);
  }

  private buildPhoneQueries(
    phone: string | null | undefined,
  ): GoogleOsintQueryVariant[] {
    if (phone == null) {
      return [];
    }

    const raw = this.sanitize(String(phone));
    if (!raw) {
      return [];
    }

    const digits = raw.replace(/\D/g, '');
    if (!digits) {
      return [];
    }

    const { countryCode, national } = this.resolvePhoneParts(digits);
    const formats = this.buildAllPhoneFormats(digits, countryCode, national);
    if (!formats.length) {
      return [];
    }

    return this.uniqueVariants([
      {
        id: 'all-formats',
        label: 'All formats',
        query: this.buildQuotedOrQuery(formats),
      },
    ]);
  }

  private resolvePhoneParts(
    digits: string,
  ): { countryCode: string; national: string } {
    const indianLocal = this.getIndianLocalNumber(digits);
    if (indianLocal) {
      return { countryCode: '91', national: indianLocal };
    }

    if (digits.length > 10) {
      return {
        countryCode: digits.slice(0, digits.length - 10),
        national: digits.slice(-10),
      };
    }

    return { countryCode: '', national: digits };
  }

  private getIndianLocalNumber(digits: string): string | null {
    if (digits.length === 12 && digits.startsWith('91')) {
      return digits.substring(2);
    }
    if (
      digits.length === 11 &&
      digits.startsWith('0') &&
      /^[6-9]/.test(digits.charAt(1))
    ) {
      return digits.substring(1);
    }
    if (digits.length === 10 && /^[6-9]/.test(digits)) {
      return digits;
    }
    return null;
  }

  private buildAllPhoneFormats(
    digits: string,
    countryCode: string,
    national: string,
  ): string[] {
    const formats = [
      digits,
      national,
      ...this.buildLocalGroupings(national),
      ...this.buildCountryCodeFormats(countryCode, national),
    ];

    if (countryCode && digits !== `${countryCode}${national}`) {
      formats.push(`${countryCode}${national}`);
    }

    return this.uniqueFormats(formats).slice(0, 4);
  }

  /** Every common way a national number is written on the web. */
  private buildLocalGroupings(national: string): string[] {
    const n = national;
    if (!n) {
      return [];
    }

    if (n.length !== 10) {
      const half = Math.floor(n.length / 2);
      return this.uniqueFormats([
        n,
        half > 0 ? `${n.slice(0, half)} ${n.slice(half)}` : '',
        half > 0 ? `${n.slice(0, half)}-${n.slice(half)}` : '',
      ]);
    }

    const a3 = n.slice(0, 3);
    const b3 = n.slice(3, 6);
    const c4 = n.slice(6);
    const a4 = n.slice(0, 4);
    const b6 = n.slice(4);
    const a5 = n.slice(0, 5);
    const b5 = n.slice(5);
    const a6 = n.slice(0, 6);
    const b4 = n.slice(6);
    const rest7 = n.slice(3);

    return this.uniqueFormats([
      n,
      `${a4} ${b6}`,
      `${a5} ${b5}`,
      `${a3} ${rest7}`,
      `${a6} ${b4}`,
      `${a3}-${rest7}`,
      `${a4}-${b6}`,
      `${a5}-${b5}`,
      `${a6}-${b4}`,
      `${a3}-${b3} ${c4}`,
      `${a3} ${b3}-${c4}`,
      `${a3}-${b3}-${c4}`,
      `${a3} ${b3} ${c4}`,
      `(${a3}) ${b3}-${c4}`,
      `(${a3})${b3}-${c4}`,
      `(${a3}) ${b3}${c4}`,
      `(${a3}) ${rest7}`,
      `(${a5}) ${b5}`,
      `${a3}.${b3}.${c4}`,
      `${a5}.${b5}`,
      `${a4}.${b6}`,
      `${a3}.${rest7}`,
    ]);
  }

  private buildCountryCodeFormats(
    countryCode: string,
    national: string,
  ): string[] {
    if (!countryCode || !national) {
      return [];
    }

    const cc = countryCode;
    const n = national;
    const formats = [
      `${cc}${n}`,
      `${cc} ${n}`,
      `${cc}-${n}`,
      `(${cc}) ${n}`,
      `(${cc})${n}`,
    ];

    if (n.length === 10) {
      const a3 = n.slice(0, 3);
      const b3 = n.slice(3, 6);
      const c4 = n.slice(6);
      const a5 = n.slice(0, 5);
      const b5 = n.slice(5);
      const nanp = `(${a3}) ${b3}-${c4}`;
      formats.push(
        `${cc} ${a3} ${b3} ${c4}`,
        `${cc}-${a3}-${b3}-${c4}`,
        `${cc} ${nanp}`,
        `${cc}-${a3}-${n.slice(3)}`,
        `${cc} ${a5} ${b5}`,
        `${cc}-${a5}-${b5}`,
      );
      if (cc === '91') {
        formats.push(`0${n}`);
      }
    }

    return this.uniqueFormats(formats);
  }

  private buildAllNameFormats(name: string): string[] {
    const parts = name.split(/\s+/).filter(Boolean);
    if (!parts.length) {
      return [];
    }

    const titleCase = parts
      .map(
        (part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
      )
      .join(' ');
    const formats: string[] = [
      name,
      parts.join(' '),
      name.toLowerCase(),
      name.toUpperCase(),
      titleCase,
    ];

    if (parts.length >= 2) {
      const first = parts[0];
      const last = parts[parts.length - 1];
      const given = parts.slice(0, -1).join(' ');
      const firstInitial = first.charAt(0);
      const lastInitial = last.charAt(0);
      const middle = parts.slice(1, -1);

      formats.push(
        parts.join('-'),
        parts.join('_'),
        parts.join('.'),
        parts.join(''),
        `${last}, ${first}`,
        `${last}, ${given}`,
        `${last} ${first}`,
        `${last} ${given}`,
        `${firstInitial}. ${last}`,
        `${firstInitial} ${last}`,
        `${first} ${lastInitial}.`,
        `${firstInitial}.${lastInitial}.`,
        `${firstInitial}. ${lastInitial}.`,
        `${firstInitial}${lastInitial}`,
        `${first} * ${last}`,
      );

      if (middle.length) {
        const middleInitials = middle
          .map((part) => `${part.charAt(0).toUpperCase()}.`)
          .join(' ');
        formats.push(
          `${first} ${middleInitials} ${last}`,
          `${firstInitial}. ${middleInitials} ${last}`,
        );
      }
    }

    return this.uniqueFormats(formats);
  }

  private buildAllUsernameFormats(username: string): string[] {
    const stripped = username.replace(/^@+/, '').trim();
    if (!stripped) {
      return [];
    }

    const tokens = stripped.split(/[._\-\s]+/).filter(Boolean);
    const formats: string[] = [
      username,
      stripped,
      `@${stripped}`,
      stripped.toLowerCase(),
      `@${stripped.toLowerCase()}`,
      `u/${stripped}`,
      `/u/${stripped}`,
      `/user/${stripped}`,
      `/users/${stripped}`,
    ];

    if (tokens.length >= 2) {
      for (const separator of ['_', '.', '-', ' ', '']) {
        const joined = tokens.join(separator);
        formats.push(joined, `@${joined}`);
      }
      const camel =
        tokens[0].toLowerCase() +
        tokens
          .slice(1)
          .map(
            (token) =>
              token.charAt(0).toUpperCase() + token.slice(1).toLowerCase(),
          )
          .join('');
      formats.push(camel, `@${camel}`);
    }

    return this.uniqueFormats(formats);
  }

  private buildQuotedOrQuery(formats: string[]): string {
    return formats.map((format) => `"${format}"`).join(' | ');
  }

  private uniqueFormats(formats: string[]): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const format of formats) {
      const value = String(format || '')
        .replace(/"/g, '')
        .replace(/^\+/, '')
        .trim();
      if (!value || this.isCompactPlusDigits(value) || seen.has(value)) {
        continue;
      }
      seen.add(value);
      out.push(value);
    }
    return out;
  }

  private isCompactPlusDigits(value: string): boolean {
    return /^\+\d+$/.test(value);
  }

  private sanitize(value: string | null | undefined): string {
    if (value == null) {
      return '';
    }
    return String(value).replace(/"/g, '').trim();
  }

  private unique(queries: string[]): string[] {
    return [...new Set(queries.map((query) => query.trim()).filter(Boolean))];
  }

  private uniqueVariants(
    variants: GoogleOsintQueryVariant[],
  ): GoogleOsintQueryVariant[] {
    const byQuery = new Map<string, GoogleOsintQueryVariant>();
    for (const variant of variants) {
      const query = variant.query.trim();
      const compactPlus = this.isCompactPlusDigits(query.replace(/^"|"$/g, ''));
      if (!query || compactPlus || byQuery.has(query)) {
        continue;
      }
      byQuery.set(query, { ...variant, query });
    }

    return this.unique([...byQuery.keys()]).map((query) => byQuery.get(query)!);
  }
}
